import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { apiClient } from '../../shared/api/client';
import { useAuth } from '../../shared/auth/AuthContext';

export function RegisterPage(): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await apiClient.post('/auth/register', {
        name,
        email,
        password,
        companyName,
      });

      // Registration auto-logs in (sets cookie).
      // Reload to let AuthContext pick up the session.
      window.location.href = '/dashboard';
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Erro ao criar conta';
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #001a33 0%, #002045 50%, #001530 100%)',
        padding: '1rem',
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: 420,
          background: '#fff',
          borderRadius: 16,
          padding: '2.5rem 2rem',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h1
          style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#002045',
            textAlign: 'center',
            marginBottom: '0.25rem',
          }}
        >
          Crie sua conta
        </h1>
        <p
          style={{
            textAlign: 'center',
            color: '#6b7280',
            fontSize: '0.875rem',
            marginBottom: '1.5rem',
          }}
        >
          Cadastre-se e comece a usar o Nexora ERP
        </p>

        {error && (
          <div
            role="alert"
            style={{
              background: '#fef2f2',
              color: '#b91c1c',
              padding: '0.75rem 1rem',
              borderRadius: 8,
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ marginBottom: '1rem' }}>
          <label
            htmlFor="reg-name"
            style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: 4 }}
          >
            Seu nome
          </label>
          <input
            id="reg-name"
            type="text"
            required
            minLength={2}
            maxLength={200}
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            placeholder="João Silva"
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label
            htmlFor="reg-email"
            style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: 4 }}
          >
            Email
          </label>
          <input
            id="reg-email"
            type="email"
            required
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            placeholder="voce@empresa.com.br"
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label
            htmlFor="reg-password"
            style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: 4 }}
          >
            Senha
          </label>
          <input
            id="reg-password"
            type="password"
            required
            minLength={8}
            maxLength={100}
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            placeholder="Mínimo 8 caracteres"
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label
            htmlFor="reg-company"
            style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: 4 }}
          >
            Nome da empresa
          </label>
          <input
            id="reg-company"
            type="text"
            required
            minLength={2}
            maxLength={200}
            value={companyName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyName(e.target.value)}
            placeholder="Minha Empresa Ltda"
            style={inputStyle}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '0.75rem',
            background: loading ? '#6b7280' : '#002045',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: '1rem',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            marginBottom: '1rem',
          }}
        >
          {loading ? 'Criando conta...' : 'Criar conta'}
        </button>

        <p style={{ textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
          Já tem uma conta?{' '}
          <Link to="/login" style={{ color: '#002045', fontWeight: 600, textDecoration: 'none' }}>
            Entrar
          </Link>
        </p>
      </form>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.625rem 0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: '0.95rem',
  outline: 'none',
  boxSizing: 'border-box',
};
